/**
 * CONSAI — Sidebar (dark, feature-colored active accents)
 */
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  LayoutDashboard, FileText, Users, Package, ShoppingCart, Receipt,
  BookOpen, User, LogOut, Lock, Crown, ChevronLeft, ChevronRight, Brain, BarChart3, Truck
} from 'lucide-react';
import { getFeatureColor } from '../lib/feature-colors';

const Sidebar = () => {
  const { t, language, toggleLanguage } = useLanguage();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);

  const isPremium = user?.current_plan === 'premium' || user?.current_plan === 'premium_trial';

  const navItems = [
    { id: 'dashboard',   label: language === 'hi' ? 'डैशबोर्ड' : 'Dashboard',          icon: LayoutDashboard, path: '/dashboard',   feature: 'neutral' },
    { id: 'customers',   label: language === 'hi' ? 'ग्राहक' : 'Customers',             icon: Users,           path: '/customers',   feature: 'customer' },
    { id: 'stock',       label: language === 'hi' ? 'स्टॉक' : 'Stock',                 icon: Package,         path: '/stock',       feature: 'stock' },
    { id: 'suppliers',   label: language === 'hi' ? 'सप्लायर' : 'Suppliers',           icon: Truck,           path: '/suppliers',   feature: 'supplier' },
    { id: 'sales',       label: language === 'hi' ? 'बिक्री' : 'Sales',                icon: ShoppingCart,    path: '/sales',       feature: 'sales' },
    { id: 'invoices',    label: language === 'hi' ? 'इनवॉइस' : 'Invoices',             icon: Receipt,         path: '/invoices',    feature: 'invoice' },
    { id: 'credit',      label: language === 'hi' ? 'उधार/खाताबुक' : 'Credit/Khatabook', icon: BookOpen,        path: '/credit',      feature: 'credit' },
    { id: 'analytics',   label: language === 'hi' ? 'AI एनालिटिक्स' : 'AI Analytics',  icon: BarChart3,       path: '/analytics',   feature: 'analytics' },
    { id: 'report',      label: language === 'hi' ? 'AI बिज़नेस रिपोर्ट' : 'AI Business Report', icon: FileText, path: '/report', locked: !user?.report_purchased, feature: 'ai' },
    { id: 'ai-assistant', label: language === 'hi' ? 'AI सहायक' : 'AI Assistant',     icon: Brain,           path: '/ai-assistant', feature: 'ai' },
    { id: 'profile',     label: language === 'hi' ? 'प्रोफाइल' : 'Profile',            icon: User,            path: '/profile',     feature: 'neutral' },
  ];

  const handleNavClick = (item) => {
    if (item.locked && item.id === 'report') { navigate('/payment?type=report'); return; }
    navigate(item.path);
  };

  const handleLogout = async () => {
    await logout();
    toast.success(language === 'hi' ? 'लॉगआउट सफल' : 'Logged out successfully');
    navigate('/login');
  };

  return (
    <>
      <aside
        className={`hidden lg:flex flex-col fixed left-0 top-0 h-full z-20 transition-all duration-300 ${collapsed ? 'w-20' : 'w-64'}`}
        style={{ background: '#0E0E10', borderRight: '1px solid rgba(255,255,255,0.06)' }}
      >
        {/* Logo */}
        <div className="p-4 border-b border-white/5 flex-shrink-0">
          <div className="flex items-center justify-center">
            <img src="https://customer-assets.emergentagent.com/job_consai-foundation/artifacts/0mnri4ba_CONSAI%20Logo_compressed.png" alt="CONSAI" className="h-10" />
          </div>
        </div>

        <nav className="flex-1 p-3 space-y-1 overflow-y-auto min-h-0">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            const fc = getFeatureColor(item.feature);

            return (
              <button
                key={item.id}
                onClick={() => handleNavClick(item)}
                className={`group relative w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all text-sm overflow-hidden`}
                style={{
                  background: isActive ? `linear-gradient(90deg, ${fc.tint} 0%, transparent 100%)` : 'transparent',
                  color: isActive ? '#FFFFFF' : '#A1A1AA',
                }}
                onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
                onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                data-testid={`nav-${item.id}`}
              >
                {/* Left accent bar when active */}
                {isActive && (
                  <span
                    className="absolute left-0 top-2 bottom-2 w-[3px] rounded-r-full"
                    style={{ background: fc.hex, boxShadow: `0 0 12px ${fc.hex}` }}
                  />
                )}
                <item.icon
                  className="h-5 w-5 flex-shrink-0 transition-colors"
                  style={{ color: isActive ? fc.hex : '#71717A' }}
                />
                {!collapsed && (
                  <>
                    <span className="flex-1 text-left font-medium">{item.label}</span>
                    {item.locked && <Lock className="h-4 w-4 text-white/30" />}
                    {item.premium && !isPremium && (
                      <Badge className="bg-amber-500/15 text-amber-300 border border-amber-500/30 text-[10px] px-1.5 py-0">
                        <Crown className="h-3 w-3" />
                      </Badge>
                    )}
                  </>
                )}
              </button>
            );
          })}
        </nav>

        <div className="p-3 border-t border-white/5 space-y-1 flex-shrink-0">
          <button
            onClick={toggleLanguage}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-white/60 hover:text-white hover:bg-white/5 text-sm transition-colors"
            data-testid="sidebar-language-toggle"
          >
            <span className="w-5 h-5 flex items-center justify-center text-white/50">{language === 'en' ? 'अ' : 'A'}</span>
            {!collapsed && <span>{language === 'en' ? 'हिंदी' : 'English'}</span>}
          </button>

          <button
            onClick={() => setShowLogoutDialog(true)}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-rose-400 hover:bg-rose-500/10 text-sm transition-colors"
            data-testid="sidebar-logout-btn"
          >
            <LogOut className="h-5 w-5" />
            {!collapsed && <span>{t('logout')}</span>}
          </button>
        </div>

        <button
          onClick={() => setCollapsed(!collapsed)}
          className="absolute -right-3 top-20 w-6 h-6 bg-[#1A1A1C] border border-white/10 text-white/70 rounded-full flex items-center justify-center hover:bg-[#2A2A2E]"
          data-testid="sidebar-collapse-btn"
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </button>
      </aside>

      <Dialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog}>
        <DialogContent className="sm:max-w-md bg-[#1A1A1C] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>{t('logout')}</DialogTitle>
            <DialogDescription className="text-white/55">{t('logoutConfirm')}</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowLogoutDialog(false)}
              className="bg-transparent border-white/10 text-white hover:bg-white/5" data-testid="cancel-logout-btn">
              {t('cancel')}
            </Button>
            <Button onClick={handleLogout}
              className="bg-rose-500 hover:bg-rose-600 text-white" data-testid="confirm-logout-btn">
              {t('logout')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Sidebar;
